import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { apiFetch } from '@/lib/apiFetch';

// ── Types ────────────────────────────────────────────────────────────────────

export interface Source {
  id: string;
  /** @deprecated removed in migration 019 */
  org_id?: string;
  jurisdiction_id?: string | null;
  /** FIPS code of the geo_place linked at provisioning time (migration 048). */
  jurisdiction_fips?: string | null;
  name: string;
  base_url: string;
  source_type: string;
  schedule_cron: string | null;
  enabled: boolean;
  last_run_at: string | null;
  /** Status of the most recent scrape job for this source. */
  last_status: 'queued' | 'running' | 'succeeded' | 'failed' | 'partial' | null;
  crawl_config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ScrapeJob {
  id: string;
  source_id: string;
  /** @deprecated removed in migration 019 */
  org_id?: string;
  jurisdiction_id?: string | null;
  status:
    | 'queued'
    | 'running'
    | 'succeeded'
    | 'failed'
    | 'partial'
    | 'paused'
    | 'cancelled';
  started_at: string | null;
  finished_at: string | null;
  stats: {
    pages_crawled?: number;
    artifacts_found?: number;
    new_artifacts?: number;
    bytes_stored?: number;
    skipped?: {
      no_extension?: number;
      no_doc_signal?: number;
      skipped_folder?: number;
      mime_type?: number;
      fetch_failed?: number;
      deduped?: number;
      robots_disallowed?: number;
    };
    /** @deprecated legacy camelCase */
    pagesCrawled?: number;
    /** @deprecated */
    artifactsFound?: number;
    /** @deprecated */
    newArtifacts?: number;
    /** @deprecated */
    bytesStored?: number;
  };
  error: string | null;
  created_at: string;
  sources?: { name: string; base_url: string };
}

export interface Artifact {
  id: string;
  /** @deprecated removed in migration 019 */
  org_id?: string;
  jurisdiction_id?: string | null;
  source_id: string;
  scrape_job_id: string | null;
  url: string;
  discovered_on_url: string | null;
  storage_path: string;
  sha256: string;
  mime_type: string | null;
  byte_size: number | null;
  http_headers: Record<string, string> | null;
  kind: string;
  first_seen_at: string;
  last_seen_at: string;
  version_of: string | null;
  created_at: string;
  department: string | null;
  meeting_type: string | null;
  meeting_date: string | null;
  relevance_score: number | null;
  classified_at: string | null;
  sources?: { name: string };
  extracted_texts?: Array<{ id: string; status: string }>;
  chunks?: Array<{ id: string }>;
  failedJobs?: Array<{
    job_type: string;
    status: string;
    error: string;
    attempts: number;
    finished_at: string;
  }>;
}

export interface SearchResultArtifact {
  id: string;
  url: string;
  kind: string;
  source_id: string;
  mime_type: string | null;
  byte_size: number | null;
  first_seen_at: string;
  last_seen_at: string | null;
  created_at: string | null;
  department: string | null;
  meeting_type: string | null;
  meeting_date: string | null;
  classified_at: string | null;
  storage_path?: string | null;
  sources: { name: string } | null;
}

export interface SearchResult {
  id: string;
  chunk_id: string;
  artifact_id: string;
  text: string;
  score: number;
  start_time: number | null;
  end_time: number | null;
  artifact: SearchResultArtifact | null;
  /** @deprecated kept for back-compat; use artifact instead */
  artifacts?: { url: string; mime_type: string | null };
}

// ── Sources hook ──────────────────────────────────────────────────────────────

export function useSources() {
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSources = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/sources');
      if (!res.ok) throw new Error(`Failed to load sources: ${res.status}`);
      const json = await res.json();
      setSources(json.data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSources();
    const interval = setInterval(() => void fetchSources(true), 300_000);
    return () => clearInterval(interval);
  }, [fetchSources]);

  const createSource = async (
    payload: Pick<
      Source,
      'name' | 'base_url' | 'source_type' | 'crawl_config'
    > & {
      schedule_cron?: string | null;
      jurisdiction_id?: string | null;
      jurisdiction_fips?: string | null;
    }
  ) => {
    const res = await apiFetch('/api/sources', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      const msg =
        (e as { message?: string })?.message ??
        (e as { error?: string })?.error ??
        'Failed to create source';
      throw new Error(msg);
    }
    const json = await res.json();
    const data = json.data as Source;
    setSources((prev) => [data, ...prev]);
    return data;
  };

  const updateSource = async (id: string, patch: Partial<Source>) => {
    const res = await apiFetch(`/api/sources/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error('Failed to update source');
    const json = await res.json();
    const data = json.data as Source;
    setSources((prev) => prev.map((s) => (s.id === id ? data : s)));
    return data;
  };

  const deleteSource = async (id: string) => {
    const res = await apiFetch(`/api/sources/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete source');
    setSources((prev) => prev.filter((s) => s.id !== id));
  };

  const runSource = async (id: string) => {
    const res = await apiFetch(`/api/sources/${id}/run`, { method: 'POST' });
    if (!res.ok) throw new Error('Failed to trigger scrape');
    const json = await res.json();
    return json.data;
  };

  const cancelSource = async (id: string) => {
    const res = await apiFetch(`/api/sources/${id}/cancel`, { method: 'POST' });
    if (!res.ok) throw new Error('Failed to cancel scrape');
    const json = await res.json();
    return json;
  };

  const cancelScrapeJob = async (jobId: string) => {
    const res = await apiFetch(`/api/scrape-jobs/${jobId}/cancel`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to cancel job');
    return res.json();
  };

  const pauseScrapeJob = async (jobId: string) => {
    const res = await apiFetch(`/api/scrape-jobs/${jobId}/pause`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to pause job');
    return res.json();
  };

  const resumeScrapeJob = async (jobId: string) => {
    const res = await apiFetch(`/api/scrape-jobs/${jobId}/resume`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to resume job');
    return res.json();
  };

  return {
    sources,
    loading,
    error,
    fetchSources,
    createSource,
    updateSource,
    deleteSource,
    runSource,
    cancelSource,
    cancelScrapeJob,
    pauseScrapeJob,
    resumeScrapeJob,
    setSources,
  };
}

// ── Scrape jobs hook ──────────────────────────────────────────────────────────

export function useScrapeJobs(sourceId?: string) {
  const [jobs, setJobs] = useState<ScrapeJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchJobs = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (sourceId) params.set('source_id', sourceId);
        const res = await apiFetch(`/api/scrape-jobs?${params.toString()}`);
        if (!res.ok) {
          throw new Error(`Failed to load jobs: ${res.status}`);
        }
        const json = await res.json();
        setJobs((json.data ?? []) as ScrapeJob[]);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load jobs');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [sourceId]
  );

  useEffect(() => {
    void fetchJobs();
  }, [fetchJobs]);

  // Auto-refresh every 10s while any job is running
  useEffect(() => {
    const hasRunning = jobs.some((j) => j.status === 'running');
    if (!hasRunning) return;
    const interval = setInterval(() => void fetchJobs(true), 10_000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  return { jobs, loading, error, refetch: fetchJobs };
}

/**
 * Poll a single worker_job by id until it reaches a terminal status.
 */
export function useJobPoller(jobId: string | null) {
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return;

    const TERMINAL = new Set(['done', 'failed']);
    let cancelled = false;

    const poll = async () => {
      while (!cancelled) {
        const { data } = await supabase
          .from('worker_jobs')
          .select('status')
          .eq('id', jobId)
          .single();
        const s = (data as { status: string } | null)?.status ?? null;
        if (!cancelled) setStatus(s);
        if (s && TERMINAL.has(s)) break;
        await new Promise<void>((r) => setTimeout(r, 2000));
      }
    };

    void poll();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const done = status === 'done' || status === 'failed';
  return { status, done };
}

// ── Artifacts hook ────────────────────────────────────────────────────────────

export function useArtifacts(
  sourceId?: string,
  urlFilter?: string,
  kindFilter?: string,
  processingFilter?: string,
  jurisdictionId?: string
) {
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const loadArtifacts = useCallback(
    async (
      p: number = 0,
      sid?: string,
      ufilter?: string,
      kfilter?: string,
      pfilter?: string,
      jid?: string
    ) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(p + 1));
        params.set('limit', String(20)); // Use constant directly
        if (sid) params.set('source_id', sid);
        if (ufilter) params.set('q', ufilter);
        if (kfilter) params.set('kind', kfilter);
        if (pfilter) params.set('processing', pfilter);
        if (jid) params.set('jurisdiction_id', jid);
        // Add cache buster for processing filter to ensure fresh data
        if (pfilter) params.set('_t', String(Date.now()));

        const res = await apiFetch(`/api/artifacts?${params.toString()}`);
        if (!res.ok) throw new Error('Failed to load artifacts');
        const json = await res.json();
        console.log('[useArtifacts] Response:', {
          total: json.pagination?.total,
          dataLength: json.data?.length,
          processingFilter: pfilter,
        });
        setArtifacts((json.data ?? []) as Artifact[]);
        setTotal(json.pagination?.total ?? 0);
        setPage(p);
      } finally {
        setLoading(false);
      }
    },
    [] // No dependencies needed - all params passed explicitly
  );

  useEffect(() => {
    void loadArtifacts(
      0,
      sourceId,
      urlFilter,
      kindFilter,
      processingFilter,
      jurisdictionId
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, urlFilter, kindFilter, processingFilter, jurisdictionId]);

  return { artifacts, loading, total, page, pageSize, loadArtifacts };
}

// ── Search hook ───────────────────────────────────────────────────────────────

export function useSearch(workspaceId?: string) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  const search = useCallback(async (q: string) => {
    setQuery(q);
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, limit: '20' });
      if (workspaceId) params.set('workspace_id', workspaceId);
      const res = await apiFetch(`/api/search?${params.toString()}`);
      if (!res.ok) throw new Error('Search failed');
      const json = await res.json();
      // API returns: { chunk_id, text, score, start_time, end_time, artifact }
      const raw = (json.data ?? []) as Array<{
        chunk_id: string;
        text: string;
        score: number;
        start_time: number | null;
        end_time: number | null;
        artifact: SearchResultArtifact | null;
      }>;
      setResults(
        raw.map((r) => ({
          id: r.chunk_id,
          chunk_id: r.chunk_id,
          artifact_id: r.artifact?.id ?? '',
          text: r.text,
          score: r.score,
          start_time: r.start_time,
          end_time: r.end_time,
          artifact: r.artifact,
          artifacts: r.artifact
            ? { url: r.artifact.url, mime_type: r.artifact.mime_type }
            : undefined,
        }))
      );
    } finally {
      setLoading(false);
    }
  }, []);

  return { results, loading, query, search };
}
